import { Badge } from "../components/ui/badge";
import type {
  ReportConfig,
  ReportFilterDef,
  ServerReportFetchParams,
  ServerReportFetchResult,
  ReportExportableRow,
} from "../components/reports/types";
import {
  donationAPI,
  institutionsAPI,
  statesAPI,
  type DonationDetail,
  type Institution,
  type PaginatedResponse,
} from "../services/api";
import { INSTITUTION_TYPES } from "../constants/institutionFormFields";
import { isWithinDateRange, formatReportDate } from "../lib/reportDateUtils";
import { parseDate } from "../lib/utils";
import { apiItemToExportRecord } from "../lib/exportReport";
import { toInstitutionTitleCase } from "@/projects/Jainboarding_User/utils/institutionFormat";

export interface InstitutionReportRow extends ReportExportableRow {
  name: string;
  type: string;
  city: string;
  state: string;
  status: string;
  trustee_name: string;
  email: string;
  phone: string;
  created_at: string;
}

export interface DonationReportRow extends ReportExportableRow {
  id: number;
  created_at: string;
  name: string;
  email: string;
  phone: string;
  organization: string;
  donation_type: string;
  amount: string;
  mode: string;
  payment_mode: string;
  payment_status: string;
  state: string;
  district: string;
  city: string;
  receipt_no: string;
  pan_number: string;
}

function getPrimaryDonationPayment(
  donation: DonationDetail
): DonationDetail["payments"][0] | undefined {
  return donation.payments?.[0];
}

function resolveDonationLocation(
  value: string | number | null | undefined,
  nameFallback?: string | null
): string {
  if (nameFallback) {
    return String(nameFallback);
  }
  if (value === null || value === undefined || value === "") {
    return "—";
  }
  return String(value);
}

function getDonationPaymentStatus(donation: DonationDetail): string {
  return getPrimaryDonationPayment(donation)?.status ?? "—";
}

function getDonationReceiptNo(donation: DonationDetail): string {
  const payment = getPrimaryDonationPayment(donation);
  return donation.receipt_no ?? payment?.receipt_no ?? "—";
}

/** CSV export: donation fields + flattened primary payment fields from API response */
function buildDonationExportData(item: DonationDetail): Record<string, string> {
  const record = apiItemToExportRecord(item);
  const payment = getPrimaryDonationPayment(item);
  if (!payment) {
    return record;
  }

  const paymentFields: Record<string, unknown> = {
    payment_id: payment.id,
    razorpay_order_id: payment.razorpay_order_id,
    razorpay_payment_id: payment.razorpay_payment_id,
    razorpay_signature: payment.razorpay_signature,
    payment_amount: payment.amount,
    payment_currency: payment.currency,
    payment_status: payment.status,
    payment_method: payment.payment_method,
    payment_receipt_no: payment.receipt_no,
    payment_created_at: payment.created_at,
  };

  const details = payment.payment_method_details;
  if (details) {
    paymentFields.payment_details_email = details.email ?? "";
    paymentFields.payment_details_contact = details.contact ?? "";
    paymentFields.payment_details_wallet = details.wallet ?? "";
    paymentFields.payment_details_vpa = details.vpa ?? "";
  }

  return {
    ...record,
    ...apiItemToExportRecord(paymentFields),
  };
}

type InstitutionApprovalItem = Institution & {
  approval_status?: string;
  contact_person?: string | null;
  city_main?: string | number | null;
  state_main?: string | number | null;
  district_main?: string | number | null;
};

function resolveInstitutionCity(item: InstitutionApprovalItem): string {
  if (item.city_main_name) {
    return String(item.city_main_name);
  }
  if (
    item.city_main !== null &&
    item.city_main !== undefined &&
    typeof item.city_main === "string"
  ) {
    return item.city_main;
  }
  if (item.city_name) {
    return String(item.city_name);
  }
  if (item.city !== null && item.city !== undefined) {
    return String(item.city);
  }
  return "—";
}

function resolveInstitutionState(item: InstitutionApprovalItem): string {
  if (item.state_main_name) {
    return String(item.state_main_name);
  }
  if (
    item.state_main !== null &&
    item.state_main !== undefined &&
    typeof item.state_main === "string"
  ) {
    return item.state_main;
  }
  if (item.state_name) {
    return String(item.state_name);
  }
  if (item.state !== null && item.state !== undefined) {
    return String(item.state);
  }
  return "—";
}

function mapInstitutionRow(
  item: InstitutionApprovalItem
): InstitutionReportRow {
  const city = resolveInstitutionCity(item);
  const state = resolveInstitutionState(item);

  return {
    id: item.id,
    exportData: apiItemToExportRecord(item),
    name: toInstitutionTitleCase(String(item.name ?? "")),
    type: String(item.type ?? "—"),
    city,
    state,
    status: String(item.approval_status ?? item.status ?? "—"),
    trustee_name: String(item.trustee_name ?? item.contact_person ?? "—"),
    email: String(item.email ?? "—"),
    phone: String(item.phone ?? "—"),
    created_at: item.created_at ?? "",
  };
}

function mapDonationRow(item: DonationDetail): DonationReportRow {
  return {
    id: item.id,
    exportData: buildDonationExportData(item),
    created_at: item.donation_date ?? item.created_at ?? "",
    name: item.name ?? "—",
    email: item.email ?? "—",
    phone: item.phone ?? "—",
    organization: item.organization ?? "—",
    donation_type: item.donation_type ?? "—",
    amount: item.amount ?? "0",
    mode: item.mode ?? "—",
    payment_mode: item.payment_mode ?? "—",
    payment_status: getDonationPaymentStatus(item),
    state: resolveDonationLocation(item.state, item.state_name),
    district: resolveDonationLocation(item.district, item.district_name),
    city: resolveDonationLocation(item.city, item.city_name),
    receipt_no: getDonationReceiptNo(item),
    pan_number: item.pan_number ?? "—",
  };
}

function statusBadge(status: string) {
  const normalized = status.toLowerCase();
  const variant =
    normalized === "approved" ||
    normalized === "captured" ||
    normalized === "success"
      ? "default"
      : normalized === "rejected" || normalized === "failed"
        ? "destructive"
        : "secondary";
  return (
    <Badge variant={variant} className="capitalize">
      {status.replace(/_/g, " ")}
    </Badge>
  );
}

function parseInstitutionApprovalsResponse(
  raw: PaginatedResponse<Institution> | unknown
): { list: Institution[]; count: number } {
  if (!raw || typeof raw !== "object") {
    return { list: [], count: 0 };
  }

  const response = raw as PaginatedResponse<Institution> & {
    data?: Institution[] | { results?: Institution[]; count?: number };
  };

  const nested = response.data;
  const results = Array.isArray(response.results)
    ? response.results
    : Array.isArray(response.data)
      ? response.data
      : nested &&
          typeof nested === "object" &&
          Array.isArray((nested as { results?: Institution[] }).results)
        ? (nested as { results: Institution[] }).results
        : [];

  const count =
    response.count ??
    response.total_count ??
    (typeof nested === "object" &&
    nested !== null &&
    !Array.isArray(nested) &&
    "count" in nested
      ? Number((nested as { count?: number }).count)
      : undefined) ??
    results.length;

  return { list: results, count };
}

function buildInstitutionApprovalApiParams(
  page: number,
  page_size: number,
  filters: Record<string, string>,
  sortKey: string
): Record<string, string | number> {
  const params: Record<string, string | number> = { page, page_size };

  const search = (filters.search ?? "").trim();
  if (search) {
    params.search = search;
  }
  if (filters.status && filters.status !== "all") {
    params.approval_status = filters.status;
  }
  if (filters.type && filters.type !== "all") {
    params.type = filters.type;
  }

  switch (sortKey) {
    case "date_asc":
      params.ordering = "created_at";
      break;
    case "name_asc":
      params.ordering = "name";
      break;
    case "name_desc":
      params.ordering = "-name";
      break;
    case "state_asc":
      params.ordering = "state_name";
      break;
    case "status_asc":
      params.ordering = "approval_status";
      break;
    case "date_desc":
    default:
      params.ordering = "-created_at";
      break;
  }

  return params;
}

function needsClientOnlyFilters(filters: Record<string, string>): boolean {
  return (
    (filters.state !== undefined &&
      filters.state !== "" &&
      filters.state !== "all") ||
    (filters.city !== undefined &&
      filters.city !== "" &&
      filters.city !== "all") ||
    (filters.dateRange !== undefined &&
      filters.dateRange !== "" &&
      filters.dateRange !== "all")
  );
}

function applyInstitutionReportFilters(
  rows: InstitutionReportRow[],
  filters: Record<string, string>
): InstitutionReportRow[] {
  const search = (filters.search ?? "").trim().toLowerCase();
  return rows.filter(row => {
    if (search) {
      const haystack = [
        row.name,
        row.trustee_name,
        row.email,
        row.city,
        row.state,
      ]
        .join(" ")
        .toLowerCase();
      if (!haystack.includes(search)) {
        return false;
      }
    }
    if (
      filters.status !== "all" &&
      row.status.toLowerCase() !== filters.status
    ) {
      return false;
    }
    if (
      filters.type !== "all" &&
      row.type.toLowerCase() !== filters.type.toLowerCase()
    ) {
      return false;
    }
    if (
      filters.state !== "all" &&
      row.state.toLowerCase() !== filters.state.toLowerCase()
    ) {
      return false;
    }
    if (
      filters.city !== "all" &&
      row.city.toLowerCase() !== filters.city.toLowerCase()
    ) {
      return false;
    }
    if (!isWithinDateRange(row.created_at, filters.dateRange ?? "all")) {
      return false;
    }
    return true;
  });
}

function sortInstitutionReportRows(
  rows: InstitutionReportRow[],
  sortKey: string
): InstitutionReportRow[] {
  const sorted = [...rows];
  sorted.sort((a, b) => {
    switch (sortKey) {
      case "date_asc":
        return (
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
      case "name_asc":
        return a.name.localeCompare(b.name);
      case "name_desc":
        return b.name.localeCompare(a.name);
      case "state_asc":
        return a.state.localeCompare(b.state);
      case "status_asc":
        return a.status.localeCompare(b.status);
      case "date_desc":
      default:
        return (
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
    }
  });
  return sorted;
}

/** GET /jbf/institution-approvals/?page=&page_size= */
async function fetchInstitutionApprovals(
  params: ServerReportFetchParams
): Promise<ServerReportFetchResult<InstitutionReportRow>> {
  const useClientPaging = needsClientOnlyFilters(params.filters);
  const apiPage = useClientPaging ? 1 : params.page;
  const apiPageSize = useClientPaging ? 5000 : params.page_size;

  const raw = await institutionsAPI.getApprovalsAll(
    buildInstitutionApprovalApiParams(
      apiPage,
      apiPageSize,
      params.filters,
      params.sortKey
    )
  );

  const { list, count: apiCount } = parseInstitutionApprovalsResponse(raw);
  let rows = list.map(mapInstitutionRow);

  if (useClientPaging) {
    rows = applyInstitutionReportFilters(rows, params.filters);
    rows = sortInstitutionReportRows(rows, params.sortKey);
    const totalCount = rows.length;
    const start = (params.page - 1) * params.page_size;
    return {
      rows: rows.slice(start, start + params.page_size),
      totalCount,
    };
  }

  return {
    rows,
    totalCount: apiCount,
  };
}

async function fetchAllDonations(): Promise<DonationReportRow[]> {
  const { donations } = await donationAPI.getDonationDetails({
    page: 1,
    page_size: 5000,
  });
  return donations.map(mapDonationRow);
}

export const institutionReportConfig: ReportConfig<InstitutionReportRow> = {
  id: "institution-report",
  title: "Institution Report",
  description:
    "Filter, sort, and export institution records across status, type, and location.",
  exportFileName: "institution-report",
  defaultSort: "date_desc",
  defaultFilterValues: {
    search: "",
    status: "all",
    type: "all",
    state: "all",
    city: "all",
    dateRange: "all",
  },
  filters: [
    {
      type: "search",
      key: "search",
      label: "Search",
      placeholder: "Institution name, trustee, email...",
    },
    {
      type: "select",
      key: "status",
      label: "Status",
      defaultValue: "all",
      options: [
        { value: "all", label: "All Status" },
        { value: "approved", label: "Approved" },
        { value: "pending", label: "Pending" },
        { value: "rejected", label: "Rejected" },
      ],
    },
    {
      type: "select",
      key: "type",
      label: "Institution Type",
      defaultValue: "all",
      options: [
        { value: "all", label: "All Types" },
        ...INSTITUTION_TYPES.map(t => ({ value: t.value, label: t.label })),
      ],
    },
    {
      type: "select",
      key: "state",
      label: "State",
      defaultValue: "all",
      options: [{ value: "all", label: "All States" }],
    },
    {
      type: "select",
      key: "city",
      label: "City",
      defaultValue: "all",
      options: [{ value: "all", label: "All Cities" }],
    },
    {
      type: "dateRange",
      key: "dateRange",
      label: "Date Range",
      defaultValue: "all",
      options: [
        { value: "all", label: "All Time" },
        { value: "today", label: "Today" },
        { value: "week", label: "This Week" },
        { value: "month", label: "This Month" },
        { value: "year", label: "This Year" },
      ],
    },
  ],
  sortOptions: [
    { value: "date_desc", label: "Newest First" },
    { value: "date_asc", label: "Oldest First" },
    { value: "name_asc", label: "Name (A–Z)" },
    { value: "name_desc", label: "Name (Z–A)" },
    { value: "state_asc", label: "State (A–Z)" },
    { value: "status_asc", label: "Status (A–Z)" },
  ],
  columns: [
    {
      key: "name",
      label: "Institution",
      accessor: r => r.name,
      className: "font-medium",
    },
    { key: "type", label: "Type", accessor: r => r.type },
    { key: "city", label: "City", accessor: r => r.city },
    { key: "state", label: "State", accessor: r => r.state },
    {
      key: "status",
      label: "Status",
      accessor: r => r.status,
      exportValue: r => r.status,
      render: r => statusBadge(r.status),
    },
    { key: "trustee", label: "Trustee", accessor: r => r.trustee_name },
    {
      key: "created_at",
      label: "Registered",
      accessor: r => formatReportDate(r.created_at),
      exportValue: r => r.created_at,
    },
  ],
  serverFetch: fetchInstitutionApprovals,
  applyFilters: applyInstitutionReportFilters,
  sortRows: sortInstitutionReportRows,
};

export const donationReportConfig: ReportConfig<DonationReportRow> = {
  id: "donation-report",
  title: "Donation Report",
  description:
    "Filter, sort, and export donation transactions with payment and donor details.",
  exportFileName: "donation-report",
  defaultSort: "date_desc",
  defaultFilterValues: {
    search: "",
    donation_type: "all",
    payment_status: "all",
    mode: "all",
    dateRange: "all",
  },
  filters: [
    {
      type: "search",
      key: "search",
      label: "Search",
      placeholder: "Donor name, email, receipt no...",
    },
    {
      type: "select",
      key: "donation_type",
      label: "Donation Type",
      defaultValue: "all",
      options: [{ value: "all", label: "All Types" }],
    },
    {
      type: "select",
      key: "payment_status",
      label: "Payment Status",
      defaultValue: "all",
      options: [
        { value: "all", label: "All Status" },
        { value: "success", label: "Success" },
        { value: "captured", label: "Captured" },
        { value: "authorized", label: "Authorized" },
        { value: "failed", label: "Failed" },
        { value: "pending", label: "Pending" },
      ],
    },
    {
      type: "select",
      key: "mode",
      label: "Payment Mode",
      defaultValue: "all",
      options: [
        { value: "all", label: "All Modes" },
        { value: "online", label: "Online" },
        { value: "offline", label: "Offline" },
      ],
    },
    {
      type: "dateRange",
      key: "dateRange",
      label: "Date Range",
      defaultValue: "all",
      options: [
        { value: "all", label: "All Time" },
        { value: "today", label: "Today" },
        { value: "week", label: "This Week" },
        { value: "month", label: "This Month" },
        { value: "year", label: "This Year" },
      ],
    },
  ],
  sortOptions: [
    { value: "date_desc", label: "Newest First" },
    { value: "date_asc", label: "Oldest First" },
    { value: "amount_desc", label: "Amount (High–Low)" },
    { value: "amount_asc", label: "Amount (Low–High)" },
    { value: "name_asc", label: "Donor (A–Z)" },
  ],
  columns: [
    {
      key: "created_at",
      label: "Date",
      accessor: r => formatReportDate(r.created_at),
    },
    {
      key: "name",
      label: "Donor",
      accessor: r => r.name,
      className: "font-medium",
    },
    { key: "email", label: "Email", accessor: r => r.email },
    { key: "phone", label: "Phone", accessor: r => r.phone },
    { key: "donation_type", label: "Type", accessor: r => r.donation_type },
    { key: "mode", label: "Mode", accessor: r => r.mode },
    {
      key: "payment_mode",
      label: "Payment Mode",
      accessor: r => r.payment_mode,
    },
    {
      key: "amount",
      label: "Amount (₹)",
      accessor: r => Number(r.amount || 0).toLocaleString("en-IN"),
    },
    {
      key: "payment_status",
      label: "Payment Status",
      accessor: r => r.payment_status,
      render: r => statusBadge(r.payment_status),
    },
  ],
  fetchData: fetchAllDonations,
  applyFilters: (rows, filters) => {
    const search = (filters.search ?? "").trim().toLowerCase();
    return rows.filter(row => {
      if (search) {
        const haystack = [
          row.name,
          row.email,
          row.phone,
          row.organization,
          row.receipt_no,
          row.donation_type,
          row.state,
          row.district,
          row.city,
          row.pan_number,
        ]
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(search)) {
          return false;
        }
      }
      if (
        filters.donation_type !== "all" &&
        row.donation_type.toLowerCase() !== filters.donation_type.toLowerCase()
      ) {
        return false;
      }
      if (
        filters.payment_status !== "all" &&
        row.payment_status.toLowerCase() !==
          filters.payment_status.toLowerCase()
      ) {
        return false;
      }
      if (
        filters.mode !== "all" &&
        row.mode.toLowerCase() !== filters.mode.toLowerCase()
      ) {
        return false;
      }
      if (!isWithinDateRange(row.created_at, filters.dateRange ?? "all")) {
        return false;
      }
      return true;
    });
  },
  sortRows: (rows, sortKey) => {
    const sorted = [...rows];
    sorted.sort((a, b) => {
      switch (sortKey) {
        case "date_asc": {
          const tA = parseDate(a.created_at)?.getTime() ?? 0;
          const tB = parseDate(b.created_at)?.getTime() ?? 0;
          return tA - tB;
        }
        case "amount_desc":
          return Number(b.amount) - Number(a.amount);
        case "amount_asc":
          return Number(a.amount) - Number(b.amount);
        case "name_asc":
          return a.name.localeCompare(b.name);
        case "date_desc":
        default: {
          const tA = parseDate(a.created_at)?.getTime() ?? 0;
          const tB = parseDate(b.created_at)?.getTime() ?? 0;
          return tB - tA;
        }
      }
    });
    return sorted;
  },
};

function uniqueLocationOptions(
  values: string[]
): { value: string; label: string }[] {
  return [...new Set(values.filter(v => v && v !== "—"))]
    .sort((a, b) => a.localeCompare(b))
    .map(v => ({ value: v, label: v }));
}

/** Hydrate state/city filter options from institution-approvals (state_main, city_main) */
export async function hydrateInstitutionReportFilters(
  rows: InstitutionReportRow[]
): Promise<ReportFilterDef[]> {
  let sourceRows = rows;

  try {
    const raw = await institutionsAPI.getApprovalsAll({
      page: 1,
      page_size: 5000,
    });
    const { list } = parseInstitutionApprovalsResponse(raw);
    if (list.length > 0) {
      sourceRows = list.map(mapInstitutionRow);
    }
  } catch {
    // use current page rows
  }

  let stateOptions = uniqueLocationOptions(sourceRows.map(r => r.state));
  const cityOptions = uniqueLocationOptions(sourceRows.map(r => r.city));

  try {
    const states = await statesAPI.list();
    const fromMaster = (states ?? [])
      .map(s => s.name ?? String(s.id))
      .filter(Boolean);
    stateOptions = uniqueLocationOptions([
      ...stateOptions.map(o => o.value),
      ...fromMaster,
    ]);
  } catch {
    // keep state options from rows only
  }

  return institutionReportConfig.filters.map(f => {
    if (f.key === "state") {
      return {
        ...f,
        options: [{ value: "all", label: "All States" }, ...stateOptions],
      };
    }
    if (f.key === "city") {
      return {
        ...f,
        options: [{ value: "all", label: "All Cities" }, ...cityOptions],
      };
    }
    return f;
  });
}

export function hydrateDonationReportFilters(
  rows: DonationReportRow[]
): ReportFilterDef[] {
  const types = [
    ...new Set(rows.map(r => r.donation_type).filter(Boolean)),
  ].sort();
  return donationReportConfig.filters.map(f =>
    f.key === "donation_type"
      ? {
          ...f,
          options: [
            { value: "all", label: "All Types" },
            ...types.map(t => ({ value: t, label: t })),
          ],
        }
      : f
  );
}
